(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  let billingSummaryOpen = false;
  let pendingRestore = null;

  function setUpdating(updating) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.toggle('is-updating', updating);
    if (!updating) qsa('[data-cart-quantity],[data-cart-remove]', drawer).forEach(button => { button.disabled = false; });
  }

  function setSummary(open, scope = document) {
    billingSummaryOpen = open;
    const billing = qs('[data-cart-billing]', scope);
    if (!billing) return;
    billing.classList.toggle('is-open', open);
    qs('[data-cart-summary-toggle]', billing)?.setAttribute('aria-expanded', String(open));
  }

  function initAnnouncements(scope = document) {
    qsa('[data-cart-announcements]', scope).forEach(announcements => {
      if (announcements.dataset.cartSliderBound === 'true') return;
      announcements.dataset.cartSliderBound = 'true';
      const slides = qsa('[data-cart-announcement-slide]', announcements);
      const dots = qsa('[data-cart-announcement-dot]', announcements);
      if (!slides.length) return;

      let activeIndex = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
      const activate = index => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
      };
      activate(activeIndex);

      if (
        slides.length > 1 &&
        announcements.dataset.autoplay !== 'false' &&
        !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ) {
        const interval = Math.max(2000, Number(announcements.dataset.interval) || 4000);
        const timer = window.setInterval(() => {
          if (!announcements.isConnected) {
            window.clearInterval(timer);
            return;
          }
          activate(activeIndex + 1);
        }, interval);
      }
    });
  }

  function initDrawer(scope = document) {
    const drawer = scope.matches?.('[data-cart-drawer]') ? scope : qs('[data-cart-drawer]', scope);
    if (!drawer) return;
    initAnnouncements(drawer);
    setSummary(billingSummaryOpen, drawer);
    if (pendingRestore) {
      const restore = pendingRestore;
      pendingRestore = null;
      requestAnimationFrame(() => {
        const scroll = qs('[data-cart-scroll]', drawer);
        const recommendations = qs('[data-cart-recommendations-track]', drawer);
        if (scroll) scroll.scrollTop = restore.scrollTop;
        if (recommendations) recommendations.scrollLeft = restore.recommendationScrollLeft;
      });
    }
    setUpdating(false);
  }

  function rememberDrawerPosition() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    pendingRestore = {
      scrollTop: qs('[data-cart-scroll]', drawer)?.scrollTop || 0,
      recommendationScrollLeft: qs('[data-cart-recommendations-track]', drawer)?.scrollLeft || 0
    };
  }

  function triggerThemeCartAdd(variantId, sourceButton) {
    if (!variantId) return;
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    rememberDrawerPosition();
    sourceButton.disabled = true;
    sourceButton.textContent = 'Adding…';
    setUpdating(true);

    const proxy = document.createElement('button');
    proxy.type = 'button';
    proxy.hidden = true;
    proxy.dataset.quickAddVariant = String(variantId);
    drawer.appendChild(proxy);

    const cleanup = () => {
      proxy.remove();
      if (sourceButton.isConnected) {
        sourceButton.disabled = false;
        sourceButton.textContent = sourceButton.dataset.defaultText || '+ Add';
      }
      setUpdating(false);
      document.removeEventListener('cart:item-added', cleanup);
      document.removeEventListener('cart:add-cancelled', cleanup);
    };
    document.addEventListener('cart:item-added', cleanup, { once: true });
    document.addEventListener('cart:add-cancelled', cleanup, { once: true });
    window.setTimeout(cleanup, 9000);
    proxy.click();
  }

  document.addEventListener('click', event => {
    const summaryToggle = event.target.closest('[data-cart-summary-toggle]');
    if (summaryToggle) {
      event.preventDefault();
      setSummary(!summaryToggle.closest('[data-cart-billing]')?.classList.contains('is-open'));
      return;
    }

    const recommendationOpen = event.target.closest('[data-cart-recommendation-open]');
    if (recommendationOpen) {
      event.preventDefault();
      const card = recommendationOpen.closest('[data-cart-recommendation-card]');
      const panel = qs('[data-cart-recommendation-panel]', card);
      qsa('[data-cart-recommendation-panel]').forEach(other => {
        if (other !== panel) other.hidden = true;
      });
      if (panel) {
        panel.hidden = false;
        recommendationOpen.setAttribute('aria-expanded', 'true');
        qs('[data-cart-recommendation-select]', panel)?.focus({ preventScroll: true });
      }
      return;
    }

    const recommendationClose = event.target.closest('[data-cart-recommendation-close]');
    if (recommendationClose) {
      event.preventDefault();
      const card = recommendationClose.closest('[data-cart-recommendation-card]');
      const panel = qs('[data-cart-recommendation-panel]', card);
      const opener = qs('[data-cart-recommendation-open]', card);
      if (panel) panel.hidden = true;
      opener?.setAttribute('aria-expanded', 'false');
      opener?.focus({ preventScroll: true });
      return;
    }

    const recommendationAdd = event.target.closest('[data-cart-recommendation-add]');
    if (recommendationAdd) {
      event.preventDefault();
      const card = recommendationAdd.closest('[data-cart-recommendation-card]');
      const selectedVariant = qs('[data-cart-recommendation-select]', card)?.value;
      const variantId = recommendationAdd.dataset.variantId || selectedVariant;
      if (!variantId) {
        const selector = qs('[data-cart-recommendation-select]', card);
        selector?.focus({ preventScroll: true });
        selector?.classList.add('has-error');
        return;
      }
      triggerThemeCartAdd(variantId, recommendationAdd);
      return;
    }

    const cartAction = event.target.closest('[data-cart-quantity],[data-cart-remove]');
    if (cartAction && cartAction.closest('[data-cart-drawer]')) {
      rememberDrawerPosition();
      qsa('[data-cart-quantity],[data-cart-remove]', cartAction.closest('[data-cart-line]')).forEach(button => { button.disabled = true; });
      setUpdating(true);
      window.setTimeout(() => setUpdating(false), 9000);
    }
  }, true);

  document.addEventListener('change', event => {
    const selector = event.target.closest('[data-cart-recommendation-select]');
    if (!selector) return;
    selector.classList.remove('has-error');
  });

  document.addEventListener('cart:updated', () => {
    qsa('[data-cart-total]').forEach(total => {
      if (!total.textContent.trim()) total.textContent = '';
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    initDrawer(drawer);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        initDrawer(drawer);
      });
    }).observe(drawer, { childList: true, subtree: true });
  });

  document.addEventListener('shopify:section:load', event => initDrawer(event.target));
})();
